/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { WidgetPage } from './widget-page-base';
import { iamLogin, iamUtils } from '../main';
import { OktaSignIn, WidgetOptions } from '@okta/okta-signin-widget';
import { iamLocalization } from '../localization';
import { iam } from '../iam';

export class UpdatePasswordPage extends WidgetPage {
  public override localize(): void {
    super.localize();
    iamLocalization.addLocalization(() => {
      iamLogin.localizeElement('pageTitle', 'setPasswordPageTitle');
      iamLogin.localizeElement('signInBack', 'signinBack');
      this.loginWidget.localize();
      iamLogin.localizeElement(document.querySelector('#newPasswordTooltip .tooltiptitle'), 'newPwReq');
      iamLogin.localizeElement('pwreqLength', 'pwreqLength');
      iamLogin.localizeElement('pwreqLower', 'pwreqLower');
      iamLogin.localizeElement('pwreqUpper', 'pwreqUpper');
      iamLogin.localizeElement('pwreqDigit', 'pwreqDigit');
      iamLogin.localizeElement('pwreqSymbol', 'pwreqSymbol');
      iamLogin.localizeElement('pwreqUsername', 'pwreqUsername');
      iamLogin.localizeElement('pwreqOldpw', 'pwreqOldpw');
      iamLogin.localizeElement(document.querySelector('#repeatPasswordTooltip .tooltiptitle'), 'repeatPwReq');
      iamLogin.localizeElement('pwreqMatch', 'pwreqMatch');
    });
  }

  public override initialize(): void {
    iamLogin.addLangOptions();
    // do all synchronous initialization first and tell widget to render, then asynchronous initialization will happen in initializeChangePasswordWidget() method
    document.getElementById('sub-head-link').addEventListener('click', () => {
      // "not foo@bar.com?" link takes user back to email page
      iamLogin.showTransitioningWidgetPage();
      iamLogin.redirectBackToEmail();
      return false;
    });
    // no bottom margin under main header because "not foo@bar.com?" link will go right below it
    document.getElementById('main-header').classList.add('noBottomMargin');

    const config: WidgetOptions = {
      baseUrl: iam.settings.domain,
      i18n: iam.settings.i18n,
      assets: {
        baseUrl: 'localization/widget',
        rewrite: assetPath => {
          return assetPath.substr(assetPath.lastIndexOf('/'));
        }
      },
      signOutLink: iam.settings.signInPage,
      features: {
        rememberMe: false,
        selfServiceUnlock: false
      },
    };

    const displayWidget = () => {
      this.loginWidget.initialize(config, () => {
        document.getElementById('loading-content').style.display = 'none';
        document.getElementById('right-pane-contents').style.display = '';
      }, (context, error) => {
        if (context.controller === 'password-reset' && error.name === 'AuthApiError') {
          iamLogin.showErrorMessage(error.message);
        }
      }, errorMessage => {
        const i18n = iam.settings.i18n[iamLocalization.getLocale().split('_')[0]];
        if (i18n) {
          if (errorMessage === i18n['errors.E0000105']) {
            // reset link is expired or used: take user to help page and show the error there
            if (iam.settings.debug) console.log('passwordResetError');

            iam.Token.setLocation(iam.settings.signInHelp + '?version=_random_version&error=' + encodeURIComponent(errorMessage) + '&login_hint=' + encodeURIComponent(this.getUsername()));
            return true; // don't let default error message handler process this one
          } else if (iamLogin.isErrorMessageShown() && iamLogin.howLongSinceErrorDisplayed() < 250) {
            // an error message was shown less than 1/4 second ago. there's an asynchronous nature to 2 error messages coming through:
            // 1 through the 'afterError' and then 1 here, so in this case, we should ignore this one.
            console.log('okta error message ignored because another one occurred right before it:', errorMessage);
            return true; // don't let default error message handler process this one
          }
        }
      }, res => {
        if (iam.settings.debug) console.log(res);

        if (res.status === 'FORGOT_PASSWORD_EMAIL_SENT') {
          // reset account initiated
          if (iam.settings.debug) console.log('FORGOT_PASSWORD_EMAIL_SENT');
          return;
        }
        if (res.status === 'UNLOCK_ACCOUNT_EMAIL_SENT') {
          // unlock account initiated
          if (iam.settings.debug) console.log('UNLOCK_ACCOUNT_EMAIL_SENT');
          return;
        }

        // user updated password successfully
        if (res.session && res.session.token && iamLogin.parseQuery(iam.Token.getLocation().search).RelayState) {
          iam.Token.setLocation(iam.settings.domain + '/login/sessionCookieRedirect?token=' + res.session.token + '&redirectUrl=' + iamLogin.parseQuery(iam.Token.getLocation().search).RelayState);
        } else {
          iam.Token.setLocation(iam.settings.signInPage);
        }
      }, err => {
        console.error(err);
        // show error on help page
        iam.Token.setLocation(iam.settings.signInHelp + '?version=_random_version&error=' + encodeURIComponent(iamLocalization.localize('passwordResetError')) + '&login_hint=' + encodeURIComponent(this.getUsername()));
      });
      document.getElementById('signInBack').addEventListener('click', event => {
        return iamLogin.signInBack(event);
      });
    }

    if (iam.Token.getLocation().search && iamLogin.parseQuery(iam.Token.getLocation().search).token) {
      // password reset: make sure there is no session for the supplied token and then display the widget
      config.recoveryToken = iamLogin.parseQuery(iam.Token.getLocation().search).token;
      const oktaSignIn = new OktaSignIn(config);
      let cb;
      oktaSignIn.authClient.closeSession().then(cb = (err?: unknown) => {
        if (err) {
          if (iam.settings.debug) console.log('Closing Session: ', err);
        }
        displayWidget();
      }).catch(cb);
    } else if (iam.Token.getLocation().search && iamLogin.parseQuery(iam.Token.getLocation().search).activationToken) {
      // activate: get state token from authn, make sure there's no session with it, and then display the widget
      const xmlhttp = new XMLHttpRequest();
      xmlhttp.onreadystatechange = () => {
        if (xmlhttp.readyState === XMLHttpRequest.DONE) {
          if (xmlhttp.status === 200) {
            if (iam.settings.debug) console.log('XMLHttpRequest', xmlhttp);
            if (iam.settings.debug) console.debug('status is ' + xmlhttp.status);
            const jsonResponse = JSON.parse(xmlhttp.responseText);
            config.stateToken = jsonResponse.stateToken;
            const oktaSignIn = new OktaSignIn(config);
            let cb;
            oktaSignIn.authClient.closeSession().then(cb = (err?: unknown) => {
              if (err) {
                if (iam.settings.debug) console.log('Closing Session: ', err);
              }
              displayWidget();
            }).catch(cb);
          } else {
            console.debug('status is ' + xmlhttp.status);
            iam.Token.setLocation(iam.settings.signInHelp + '?version=_random_version&error=' + encodeURIComponent(iamLocalization.localize('passwordResetError')) + '&login_hint=' + encodeURIComponent(this.getUsername()));
          }
        }
      };
      xmlhttp.open('POST', iam.settings.domain + '/api/v1/authn', true);
      xmlhttp.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
      xmlhttp.send(JSON.stringify({ token: iamLogin.parseQuery(iam.Token.getLocation().search).activationToken }));
    } else {
      // no token in url, so go back to email page
      iam.Token.setLocation(iam.settings.signInPage);
    }
  }

  public override validateForm() {
    this.loginWidget.validateForm();
  }

  public override localeChanged(locale: string) {
    super.localeChanged(locale);
    this.loginWidget.localeChanged(locale);
  }
}
